<template>
    <div class="automations-page">
        <loader v-if="is('loading')"></loader>
        <div class="container-fluid" v-show="!is('loading')">
            <div>
                <h1 class="mt-4">
                    {{ t('automations-list-page_header') }}
                    <router-link
                        class="btn btn-primary pull-right"
                        to="/automations/new"
                        style="display: flex; gap: 4px; align-items: center; justify-content: center;"
                        :aria-label="t('automations-list-add_action_button')"
                    >
                        <Icon icon-class="ui-plus" size="5" />
                        <span>{{ t('automations-list-add_action_button') }}</span>
                    </router-link>
                </h1>
                <div v-if="loadedCredits" class="mt-4">
                    <div v-if="balance <= 0" class="alert alert-danger">
                        <span class="fa fa-exclamation-triangle"></span> {{ t('automations-list-no_credits_alert') }}
                    </div>
                    <div v-else-if="lowOnCredits" class="alert alert-warning">
                        <span class="fa fa-exclamation-triangle"></span> {{ t('automations-list-low_credits_alert') }}
                    </div>
                </div>
                <confirm
                    id="confirmDeleteAutomation"
                    ref="confirmDeleteAutomation"
                    @confirm="deleteAutomation"
                >
                    <template slot="title">
                        <span v-html="getConfirmationTitle('automations-list-confirm_delete_title', automationToShowInModal.title)"></span>
                    </template>
                    <template slot="body">
                        <div>
                            {{ t('automations-list-confirm_delete_body') }}
                        </div>
                    </template>
                </confirm>
                <confirm
                    id="confirmPlayAutomation"
                    ref="confirmPlayAutomation"
                    @confirm="pauseAndPlay(automationToShowInModal)"
                >
                    <template slot="title">
                        <span v-html="getConfirmationTitle('automations-list-confirm_play_title', automationToShowInModal.title)"></span>
                    </template>
                    <template slot="body">
                        <div>
                            {{ t('automations-list-confirm_play_body') }}
                        </div>
                    </template>
                </confirm>
                <confirm
                    id="confirmPauseAutomation"
                    ref="confirmPauseAutomation"
                    @confirm="pauseAndPlay(automationToShowInModal, false)"
                >
                    <template slot="title">
                        <span v-html="getConfirmationTitle('automations-list-confirm_pause_title', automationToShowInModal.title)"></span>
                    </template>
                    <template slot="body">
                        <div>
                            {{ t('automations-list-confirm_pause_body') }}
                        </div>
                    </template>
                </confirm>
                <confirm
                    id="confirmDuplicateAutomation"
                    ref="confirmDuplicateAutomation"
                    @confirm="duplicate(automationToShowInModal)"
                >
                    <template slot="title">
                        <span v-html="getConfirmationTitle('automations-list-confirm_duplicate_title', automationToShowInModal.title)"></span>
                    </template>
                    <template slot="body">
                        <div v-if="automationToShowInModal.trigger !== 'specific_date'" v-html="t('automations-list-confirm_duplicate_body')">
                        </div>
                        <div v-else v-html="t('automations-list-confirm_duplicate_campaign_body')">
                        </div>
                    </template>
                </confirm>
                <filter-bar
                    :has-branch-filter="false"
                    :has-form-filter="false"
                    :has-period-filter="false"
                    :show-labels="true"
                    :update-query-string="true"
                    @changed="onFiltersChanged"
                    :filters="extraFilters"
                    ref="filterBar"
                />
                <loader v-if="loading"></loader>
                <template v-if="automations.length > 0">
                    <div class="automations" v-show="!loading">
                        <div
                            v-for="automation in automations"
                            :key="automation.id"
                            class="automation"
                            :class="{ paused: !automation.active }"
                        >

                            <div class="box">
                                <Icon :icon-class="getTriggerIcon(automation.trigger)" class="trigger-icon" size="13" />
                                <div class="box-left">
                                    <div class="box-left-info">
                                        <div class="automation-title-row">
                                            <h2>
                                                <a :href="'/#/automations/edit/' + automation.id" :aria-label="t('automations-list-aria-edit_automation', [automation.title])">
                                                    {{ automation.title }}
                                                </a>
                                            </h2>
                                            <span
                                                v-if="automation.aborted && automation.aborted_reason"
                                                class="automation-error-icon automation-error-icon--desktop"
                                            >
                                                <KvTooltip
                                                    :text="getAutomationErrorMessage(automation)"
                                                    placement="right"
                                                    :delay="400"
                                                >
                                                    <Icon
                                                        icon-class="ui-alert-triangle"
                                                        size="3"
                                                    />
                                                </KvTooltip>
                                            </span>
                                        </div>
                                        <div
                                            v-if="automation.aborted && automation.aborted_reason"
                                            class="automation-error-inline"
                                        >
                                            <Icon icon-class="ui-alert-triangle" size="2" />
                                            {{ getAutomationErrorMessage(automation, true) }}
                                        </div>
                                        <div>
                                            <span v-if="automation.trigger !=='specific_date'">
                                                <Icon icon-class="ui-route" size="1" />
                                                {{ $root.t("automations-triggers-" + automation.trigger) }}
                                            </span>
                                            <span v-else :style="getCampaignStyle(automation)">
                                                <Icon :icon-class="getCampaignIcon(automation)" size="1" />
                                                {{ t('automations-list-campaign_label') }}
                                                <template v-if="automation.date">
                                                    <human-date
                                                        :date="automation.date"
                                                        :timezone="placeTimezone"
                                                        :format="t('dates-format_date_time_comma')"
                                                    />
                                                </template>
                                            </span>
                                            <Icon
                                                icon-class="uiarrow-narrow-right"
                                                style="color: var(--secondary);"
                                                size="2"
                                            />
                                            <div class="actions-flow">
                                                <template v-if="automation.actions && automation.actions.length > 0">
                                                    <span v-for="(action, idx) in automation.actions.slice(0, 2)" :key="`action-${automation.id}-${idx}`" class="action-item">
                                                        {{ action.alias }}
                                                        <Icon
                                                            v-if="idx < Math.min(automation.actions.length - 1, 1)"
                                                            icon-class="uiarrow-narrow-right"
                                                            style="color: var(--secondary);"
                                                            size="2"
                                                        />
                                                    </span>
                                                    <template v-if="automation.actions.length > 2">
                                                        <span class="actions-ellipsis">...</span>
                                                        <div class="actions-counter">
                                                            +{{ automation.actions.length - 2 }}
                                                        </div>
                                                    </template>
                                                </template>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="actions-menu" role="group" :aria-label="t('automations-list-aria-actions_group', [automation.title])">
                                        <!-- Actions for campaigns (specific_date) -->
                                        <template v-if="automation.trigger === 'specific_date'">
                                            <button
                                                @click.prevent="openConfirmDuplicateAutomation(automation)"
                                                :aria-label="t('automations-list-aria-duplicate_automation', [automation.title])"
                                                class="action-btn"
                                            >
                                                <Icon icon-class="ui-copy-03" size="5" :tooltip="t('automations-list-tooltip-duplicate')" />
                                            </button>
                                            <router-link
                                                v-if="automation.goal === 'orders'"
                                                :to="'/automations/report/' + automation.id"
                                                :aria-label="t('automations-list-aria-report_automation', [automation.title])"
                                                class="action-btn"
                                            >
                                                <Icon icon-class="ui-bar-chart-07" size="5" :tooltip="t('automations-list-tooltip-report')" />
                                            </router-link>
                                            <template v-if="automation.active || automation.aborted || automation.executions === '0'">
                                                <a :href="'/#/automations/edit/' + automation.id" :aria-label="t('automations-list-aria-edit_automation', [automation.title])" class="action-btn">
                                                    <Icon icon-class="ui-edit-05" size="5" :tooltip="t('automations-list-tooltip-edit')" />
                                                </a>
                                                <button
                                                    @click.prevent="confirmDelete(automation)"
                                                    :aria-label="t('automations-list-aria-remove_automation', [automation.title])"
                                                    class="action-btn action-btn--danger"
                                                >
                                                    <Icon icon-class="ui-trash-01" size="5" :tooltip="t('automations-list-tooltip-remove')" />
                                                </button>
                                            </template>
                                        </template>

                                        <!-- Actions for automations (not specific_date) -->
                                        <template v-else>
                                            <button
                                                v-if="!automation.active"
                                                @click.prevent="openConfirmPlayAutomation(automation)"
                                                :aria-label="t('automations-list-aria-resume_automation', [automation.title])"
                                                class="action-btn"
                                            >
                                                <Icon icon-class="ui-play-circle" size="5" :tooltip="t('automations-list-tooltip-resume')" />
                                            </button>
                                            <button
                                                v-if="automation.active"
                                                @click.prevent="openConfirmPauseAutomation(automation)"
                                                :aria-label="t('automations-list-aria-pause_automation', [automation.title])"
                                                class="action-btn"
                                            >
                                                <Icon icon-class="ui-pause-circle" size="5" :tooltip="t('automations-list-tooltip-pause')" />
                                            </button>
                                            <button
                                                @click.prevent="openConfirmDuplicateAutomation(automation)"
                                                :aria-label="t('automations-list-aria-duplicate_automation', [automation.title])"
                                                class="action-btn"
                                            >
                                                <Icon icon-class="ui-copy-03" size="5" :tooltip="t('automations-list-tooltip-duplicate')" />
                                            </button>
                                            <router-link
                                                v-if="automation.goal === 'orders'"
                                                :to="'/automations/report/' + automation.id"
                                                :aria-label="t('automations-list-aria-report_automation', [automation.title])"
                                                class="action-btn"
                                            >
                                                <Icon icon-class="ui-bar-chart-07" size="5" :tooltip="t('automations-list-tooltip-report')" />
                                            </router-link>
                                            <a :href="'/#/automations/edit/' + automation.id" :aria-label="t('automations-list-aria-edit_automation', [automation.title])" class="action-btn">
                                                <Icon icon-class="ui-edit-05" size="5" :tooltip="t('automations-list-tooltip-edit')" />
                                            </a>
                                            <button
                                                v-if="!automation.active"
                                                @click.prevent="confirmDelete(automation)"
                                                :aria-label="t('automations-list-aria-remove_automation', [automation.title])"
                                                class="action-btn action-btn--danger"
                                            >
                                                <Icon icon-class="ui-trash-01" size="5" :tooltip="t('automations-list-tooltip-remove')" />
                                            </button>
                                        </template>
                                    </div>
                                </div>
                                                                <div class="box-right">
                                        <div class="stats">
                                            <div v-if="hasMessageAction(automation)" class="stat stat-pill text-secondary messages">
                                                <Icon icon-class="ui-message-chat-square" size="2" />
                                                <router-link :to="getMessagesUrl(automation)">
                                                    {{ formatNumber(automation.incentives, 0) }}
                                                    {{ t('automations-list-messages_label') }}
                                                </router-link>
                                            </div>
                                            <div v-else class="stat stat-pill text-secondary">
                                                <Icon icon-class="ui-message-chat-square" size="2" />
                                                <span>
                                                    {{ formatNumber(automation.incentives, 0) }}
                                                    {{ t('automations-list-messages_label') }}
                                                </span>
                                            </div>
                                            <div class="stat text-secondary stat-pill">
                                                <Icon v-if="automation.goal === 'orders'" icon-class="ui-shopping-cart-03" size="2" />
                                                <Icon v-else-if="automation.goal === 'answers'" icon-class="ui-message-text-square-01" size="2" />
                                                <Icon v-else icon-class="ui-check-square-broken" size="2" />
                                                <div>
                                                    {{
                                                        automation.goal === "orders" ?
                                                        formatNumber(automation.incentivized_orders, 0)
                                                        : formatNumber(automation.conversions, 0)
                                                    }}
                                                    <span v-if="automation.goal === 'orders'">
                                                        {{ t('automations-list-purchases_label') }}
                                                    </span>
                                                    <span v-else-if="automation.goal === 'answers'">
                                                        {{ t('automations-list-answers_label') }}
                                                    </span>
                                                    <span v-else>
                                                        {{ t('automations-list-conversions_label') }}
                                                    </span>
                                                    <span v-if="automation.incentives" >
                                                        ( {{((automation.conversions / automation.incentives) * 100 ).toFixed(2) }}% )
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="stat text-secondary">
                                                <span
                                                    :class="
                                                        automation.active &&
                                                        automation.incentivized_revenue >
                                                            0
                                                            ? 'text-success incentivized'
                                                            : 'text-secondary incentivized'
                                                    "
                                                    v-if="automation.incentivized_orders > 0 || automation.goal === 'orders'"
                                                >
                                                    <Icon icon-class="ui-bank-note-03" size="2" />
                                                    <span class="money-text">{{ formatCurrency(automation.incentivized_revenue) }}</span>
                                                </span>
                                            </div>
                                        </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
              <div class="pages pt-4" v-show="!loading">
                <pagination
                  v-if="totalAutomations > 0"
                  :currentPage="page"
                  :perPage="10"
                  :total="totalAutomations"
                  @setPage="setPage"
                />
              </div>
            </div>
        </div>
        <div class="clear"></div>
    </div>
</template>
<script>
import keviApi from "../keviApi";
import HumanDate from "../components/human-date/human-date";
import FilterBar from "../components/reports/filter-bar";
import Icon from "../components/icon/icon";
import KvTooltip from "../components/ui/kv-tooltip.vue";
import currencyMixin from "../mixins/currency-mixin";

export default {
    mixins: [currencyMixin],
    components: { FilterBar, HumanDate, Icon, KvTooltip },
    props: {
        user: Object,
    },
    data: function () {
        return {
            automations: [],
            scheduledAutomations: [],
            automationToShowInModal: {
                id: null,
                title: null,
            },
            loading: false,
            state: "loading",
            balance: 0,
            credits_per_month: 0,
            loadedCredits: false,
            page: null,
            totalAutomations: 0,
            extraFilters: [],
            filters: {},
        };
    },
    created: function () {
        document.getElementsByTagName("title")[0].innerText =
            this.t("automations-list-page_title");
        this.getBalance();
    },
    computed: {
        placeTimezone() {
            return (
                (this.$store.state.place && this.$store.state.place.timezone) ||
                "America/Sao_Paulo"
            );
        },
        globalAdmin() {
            return this.$store.state.user.global_admin;
        },
        lowOnCredits() {
            return this.balance / this.credits_per_month < 0.2;
        },
    },
    mounted() {
        this.initFiltersFromUrl();

        const filterBar = this.$refs.filterBar;
        this.extraFilters.push({
            component: "period",
            label: this.$root.t('filter_bar-period'),
            removable: true,
            name: "period",
        });
        this.extraFilters.push({
          component: "textfield",
          label: this.$root.t('filter_bar-automation_title'),
          name: "title",
          removable: false,
        });

      filterBar.addFilter("title");

      this.extraFilters.push({
            component: "selectbox",
            label: this.$root.t('filter_bar-type'),
            name: "automation-type",
            options: [
                { value: "campaign", label: this.$root.t('filter_bar-campaigns') },
                { value: "automation", label: this.$root.t('filter_bar-automations') },
            ],
            removable: false,
        });

      filterBar.addFilter("automation-type");
        this.extraFilters.push({
            component: "selectbox",
            label: this.$root.t('filter_bar-status'),
            name: "status",
            options: [
                { value: "active", label: this.$root.t('filter_bar-active') },
                { value: "paused", label: this.$root.t('filter_bar-paused') },
                { value: "deleted", label: this.$root.t('filter_bar-deleted') },
            ],
            removable: true,
        });

        this.$nextTick(() => {
          this.setState("ready");
          const query = this.$route.query;
          this.page = query.page ? parseInt(query.page) : 1;
        });
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        escapeHtml(value) {
            return String(value ?? "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        },
        getConfirmationTitle(term, value) {
            return this.t(term, [this.escapeHtml(value)]);
        },
        initFiltersFromUrl() {
            const query = this.$route.query;
            if (query.filter) {
                try {
                    this.filters = JSON.parse(query.filter);
                } catch (e) {
                    this.filters = {};
                }
            } else {
                this.filters = {};
            }
        },
        onFiltersChanged(filters) {
            if (this.state !== 'ready') return;

            const filtersChanged = JSON.stringify(filters) !== JSON.stringify(this.filters);

            this.filters = filters;

            if (filtersChanged) {
                this.setPage(1);
            }

            this.loadData();
        },
        getTriggerIcon(trigger) {
            const iconMap = {
                // Inactivity events
                "inactivity": "ui-ghost",

                // Order events
                "new_order": "ui-package-plus",
                "order_status_change": "ui-refresh-cw-02",

                // Product events
                "product_group_stock": "ui-alert-circle",
                "too_long_on_external_status": "ui-package-search",

                // Date events
                "periodic": "ui-calendar-check-02",
                "client_date": "ui-calendar",
                "specific_date": "ui-announcement-02",

                // Profile and client events
                "data_filled": "ui-users-check",

                // Feedback events
                "feedback_received": "ui-message-check-square",

                // Coupon events
                "coupon_expiring": "ui-sale-03",

                // Reward events
                "reward_reached": "ui-award-04",
                "reward_used": "ui-award-01",

                // Custom events
                "custom_event": "ui-check-done-02",
                "custom_event_without_order": "ui-cursor-box",

                // Birthday events
                "birthday": "ui-cake",

                // Segmentation events
                "joined_segment": "ui-users-left",
                "left_segment": "ui-users-right",
                "enter_stage": "ui-users-up",
                "task_moved_to_column": "ui-check-done-02",

                // Quotation events
                "new_quotation": "ui-bank-note-01",
                "quotation_expired": "ui-credit-card-x",
                "quotation_status_change": "ui-credit-card-refresh",
                "quotations_too_long_status": "ui-credit-card-lock",
                "quotation_without_order": "ui-clock-snooze",

                // Time/status events
                "too_long_on_status": "ui-clock-snooze",
            };

            return iconMap[trigger] || "ui-file-02";
        },
        getCampaignIcon(automation) {
            if (!automation.date) return "ui-calendar";

            const campaignDate = new Date(automation.date);
            const now = new Date();

            if (campaignDate >= now) {
                return "ui-clock";
            }

            return "ui-check";
        },
        getItemTypeName(automation) {
            const isCampaign = automation && automation.trigger === "specific_date";
            return isCampaign
                ? this.t("automations-list-campaign_label")
                : this.t("automations-list-automation_label");
        },
        getFilterName(abortedReason) {
            const filterMap = {
                points_program_inactive: "automations-form-filters-points_balance",
                segment_unavailable: "automations-form-filters-client_segments",
            };
            const key = filterMap[abortedReason];
            return key ? this.t(key) : "";
        },
        formatErrorMessage(abortedReason, automation, isShort = false) {
            const typeName = this.getItemTypeName(automation);
            const genericKey = isShort ? "automations-error-short-generic" : "automations-error-generic";

            if (!abortedReason) {
                return this.t(genericKey, [typeName]);
            }

            const filterName = this.getFilterName(abortedReason);
            const prefix = isShort ? "automations-error-short-" : "automations-error-";
            const key = `${prefix}${abortedReason}`;
            let message = this.t(key, [typeName, filterName]);

            if (message === key) {
                const fallbackKey = isShort
                    ? `automations-error-${abortedReason}`
                    : `automations-error-short-${abortedReason}`;
                message = this.t(fallbackKey, [typeName, filterName]);
            }

            return message !== key ? message : this.t(genericKey, [typeName]);
        },
        getAutomationErrorMessage(automation, isShort = false) {
            if (!automation || !automation.aborted_reason) {
                return null;
            }

            return this.formatErrorMessage(automation.aborted_reason, automation, isShort);
        },
        getCampaignStyle(automation) {
            if (!automation.date) return {};

            const campaignDate = new Date(automation.date);
            const now = new Date();

            if (campaignDate >= now) {
                return { color: "#D97E08" };
            }
            return {};
        },
        setPage(page) {
          this.page = page;
          this.$nextTick(() => {
            const query = {...this.$route.query};
            query.page = this.page;
            this.$router.replace({ query: query }).catch(() => {});
          });
        },
        openConfirmDuplicateAutomation(automation) {
            this.automationToShowInModal = automation;
            this.$refs["confirmDuplicateAutomation"].open();
        },
        openConfirmPlayAutomation(automation) {
            this.automationToShowInModal = automation;
            this.$refs["confirmPlayAutomation"].open();
        },
        openConfirmPauseAutomation(automation) {
            this.automationToShowInModal = automation;
            this.$refs["confirmPauseAutomation"].open();
        },
        getProperties(action) {
            return JSON.parse(action.properties);
        },
        getMessagesUrl(automation) {
            const params = {
              automation_id: automation.id,
            };
            const filter = this.$route.query?.filter;
            if (filter) {
                const period = JSON.parse(filter)?.period;
                if (period) {
                  params.period = period;
                }
            }
            return "/notifications?filter="+JSON.stringify(params);
        },
        formatNumber(number, decimals = 2) {
            const isNumber = !isNaN(parseFloat(number)) && isFinite(number);
            if (!isNumber) {
                number = 0;
            }
            return parseFloat(number).toLocaleString("pt-BR", {
                maximumFractionDigits: decimals,
                minimumFractionDigits: decimals,
            });
        },
        hasMessageAction(automation) {
            const actions = Array.isArray(automation.actions) ? automation.actions : [];
            return actions.some(a => a && a.type === "notification");
        },
        getBalance: function () {
            const options = keviApi.getOptions(
                "transactions/get-balance",
                "GET",
            );
            this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.balance = response.body.balance;
                    this.credits_per_month = response.body.credits_per_month;
                    this.loadedCredits = true;
                }
            });
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        confirmDelete: function (item) {
            this.automationToShowInModal = item;
            this.$refs.confirmDeleteAutomation.open();
        },
        pauseAndPlay(automation, play = true) {
            let options = keviApi.getOptions(
                `automations/pause-and-play/${automation.id}`,
                "POST",
                { active: play },
            );

            this.$http(options).then((response) => {
                if (response.body.success == 1) {
                    let action = play ? "Play" : "Pause";
                    window.mixpanel?.track(`${action} Automation`, {
                        automation_id: automation.id,
                        automation_title: automation.title,
                        automation_trigger: automation.trigger,
                        automation_active: automation.active,
                        automation_conditions: automation.conditions,
                        automation_actions: automation.actions,
                        automation_target: automation.target,
                    });

                    this.loadData();
                    if (play) {
                        this.$store.dispatch(
                            "notification/success",
                            this.t("automations-list-play_success"),
                        );
                        return;
                    }

                    this.$store.dispatch(
                        "notification/success",
                        this.t("automations-list-pause_success"),
                    );
                    return;
                }
                this.$store.dispatch(
                    "notification/error",
                    this.t("automations-list-action_error"),
                );
            }).catch((error) => {
                this.loadData();
                const errorReason = error?.body?.error;
                const message = this.formatErrorMessage(errorReason, automation, true);
                this.$store.dispatch("notification/error", message);
            });
        },
        deleteAutomation: function () {
            this.$http(
                keviApi.getOptions("automations/" + this.automationToShowInModal.id, "DELETE"),
            ).then(() => {
                this.loadData();
            });
        },
        duplicate(automation) {
            let options = keviApi.getOptions(
                `automations/${automation.id}/duplicate`,
                "POST",
            );
            this.$http(options).then((response) => {
                this.$store.dispatch(
                    "notification/success",
                    this.t("automations-list-duplicate_success"),
                );
                this.$router.push(`/automations/edit/${response.body.id}`);
            });
        },
        loadData() {
            this.loading = true;

            const options = keviApi.getOptions("automations", "GET", {
                filters: this.filters,
                page: this.page,
            });

            this.$http(options).then((response) => {
                const automations = [];

                for (const a in response.body.automations) {
                    const automation = response.body.automations[a];
                    automation.active = automation.active === "1";
                    automation.aborted = automation.aborted === "1";

                    if (automation.trigger === "specific_date") {
                        const conditions = JSON.parse(automation.conditions || "[]");
                        const date = conditions?.[0]?.date;
                        if (date) automation.date = date;
                    }

                    automations.push(automation);
                }

                this.totalAutomations = response.body.total;
                this.automations = automations;
                this.setState("ready");
            }).finally(() => {
                this.loading = false;
                this.$nextTick(() => {
                    this.checkOverflow();
                });
            });
        },
        checkOverflow() {
            setTimeout(() => {
                const actionsFlows = document.querySelectorAll(".actions-flow");
                actionsFlows.forEach(element => {
                    const isOverflowing = element.scrollWidth > element.clientWidth;

                    if (isOverflowing) {
                        element.classList.add("has-overflow");
                    } else {
                        element.classList.remove("has-overflow");
                    }
                });
            }, 100);
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
    },
    watch: {
        page: function () {
            this.loadData();
        }
    }
};
</script>
<style scoped lang="scss">
.automations {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 40px;
    width: 100%;

    h2 {
        text-transform: capitalize !important;
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 4px;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        a {color: #374151;}
    }

    .automation {
        position: relative;

        .box {
            position: relative;
            display: flex;
            background-color: white;
            border: 1px solid #E5E7EB;
            border-radius: 24px;
            padding: 24px;
            width: 100%;
            align-items: center;

            &:hover { display: flex; }

            .box-right {
                flex-shrink: 0;
                flex-grow: 0;
                display: flex;
                align-items: center;
                justify-content: flex-end;
            }
        }

        .trigger-icon {
            position: absolute;
            top: calc(50% - 19px);
            left: 24px;
            background-color: var(--secondary);
        }

        .text-success { font-weight: 500; }

        .stats {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            justify-content: flex-end;
        }

        .stat-pill {
            display: flex;
            padding: 8px 12px;
            justify-content: center;
            align-items: center;
            gap: 8px;
            border-radius: 24px;
            border: 1px solid #F3F4F6;
            background-color: #FFF;
            font-size: 12px;
            font-weight: 500;
            color: #374151 !important;
            white-space: nowrap;

            a { color: #374151 !important; }
        }

        .actions-flow {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #374151;
            line-height: 1;
            overflow: hidden;
            white-space: nowrap;
            font-size: 12px;
            position: relative;
            flex: 1;
            min-width: 0;

            .action-item {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                flex-shrink: 0;
            }

            .actions-ellipsis {
                color: #9CA3AF;
                font-weight: 500;
            }

            .actions-counter {
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 20px;
                height: 20px;
                background-color: #F3F4F6;
                border: 1px solid #E5E7EB;
                border-radius: 50%;
                font-size: 10px;
                font-weight: 600;
                color: #6B7280;
                margin-left: 4px;
                padding: 4px;
                flex-shrink: 0;
            }

            &.has-overflow::after {
                content: "";
                position: absolute;
                right: 0;
                top: 0;
                bottom: 0;
                width: 50px;
                background: linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1));
                pointer-events: none;
                z-index: 1;
            }
        }

        &.paused .actions-flow {
            .actions-counter {
                background-color: #E5E7EB;
                border-color: #D1D5DB;
                color: #9CA3AF;
            }

            &.has-overflow::after {
                background: linear-gradient(to right, rgba(239,241,243,0), rgba(239,241,243,1));
            }
        }

        .incentivized {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 4px;
            border-radius: 24px;
            border: 1px solid var(--secondary);
            background: #F1FCF3;
            font-weight: 500;
            padding: 8px 12px;
            border-radius: 24px;
        }

        .incentivized.text-success,
        .incentivized.text-secondary {
            .money-text { color: #374151; font-size: 12px; }
            span.ui-icon { color: var(--secondary) !important; }
        }

        .messages {
            span {
                text-decoration: underline;
                cursor: pointer;
            }
        }

        .box-left {
            margin-top: 0;
            display: flex;
            flex-shrink: 1;
            flex-grow: 1;
            margin-left: 36px;
            justify-content: space-between;
            align-items: center;
            gap: 12px;

            .box-left-info {
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: 4px;
                overflow: hidden;
                margin-left: 24px;

                span {
                    font-weight: 500;
                }

                div {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 12px;
                    overflow: hidden;
                    min-width: 0;
                }

                .automation-title-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    min-width: 0;

                    h2 {
                        margin-bottom: 0;
                        min-width: 0;
                        line-height: 1.2;
                        display: inline-flex;
                        align-items: center;

                        a {
                            display: inline-flex;
                            align-items: center;
                        }
                    }
                }

                .automation-error-icon {
                    display: inline-flex;
                    flex-shrink: 0;
                    align-items: center;
                    justify-content: center;
                    color: #b54708;
                    cursor: default;
                    line-height: 1;

                    span.ui-icon {
                        color: #b54708 !important;
                    }

                    &--desktop {
                        @media (max-width: 768px) {
                            display: none !important;
                        }
                    }
                }

                .automation-error-inline {
                    display: none;
                    align-items: flex-start;
                    gap: 6px;
                    font-size: 12px;
                    color: #b54708;
                    background: rgba(181, 71, 8, 0.08);
                    border-radius: 6px;
                    padding: 6px 10px;
                    margin-top: 6px;
                    margin-bottom: 2px;
                    line-height: 1.4;
                    width: fit-content;
                    max-width: 100%;

                    span.ui-icon {
                        color: #b54708 !important;
                    }

                    @media (max-width: 768px) {
                        display: flex !important;
                    }
                }
            }
        }

        &.paused .actions-menu {
            background: #EFF1F3;
        }

        // Estado pausado para dispositivos touchable
        @media (hover: none) and (pointer: coarse) {
            &.paused .actions-menu {
                background: #EFF1F3;
            }
        }

        .actions-menu {
            position: absolute;
            top: -25%;
            right: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
            background: white;
            border: 1px solid #E5E7EB;
            padding: 4px;
            border-radius: 16px;
            opacity: 0;
            pointer-events: none;
            visibility: hidden;
            z-index: 8;
        }

        &:hover .actions-menu,
        &:focus-within .actions-menu {
            opacity: 1;
            pointer-events: auto;
            visibility: visible;
        }

        @media (hover: none) and (pointer: coarse) {
            .actions-menu {
                position: absolute;
                top: 100%;
                right: 0;
                opacity: 1;
                pointer-events: auto;
                visibility: visible;
                margin-top: 0;
                margin-left: 0;
                z-index: 8;
            }
        }

        .action-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: transparent;
            border: none;
            color: #374151;
            padding: 8px 8px 4px 8px;

            &--danger {
                color: #DC2626;
            }
        }
    }

    .automation.paused .box { background: #EFF1F3; }

    .automation.paused .stat-pill,
    .automation.paused .incentivized {
        background: #D2D6DB;
        border: none;
    }

    .automation.paused .incentivized.text-secondary span.ui-icon {
        color: #374151 !important;
    }

    .automation.paused .trigger-icon {
        background-color: #374151 !important;
    }

    @media (max-width: 1200px) {
        h1 {
            a { font-size: 12px; }

            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: -15px;
        }

        .automations {
            .automation {
                .box {
                    flex-direction: column-reverse;
                    align-items: flex-start;
                    padding: 20px;

                    .box-right {
                        justify-content: flex-start;
                        margin-bottom: 8px;
                        margin-left: 43px;
                    }
                }

                .actions-menu {
                    top: -17%;
                }
            }
        }
    }

    @media (max-width: 400px) {
        .automations { grid-gap: 75px; }
        .automations .automation .box-left,
        .automations .automation .box .box-left .box-left-info,
        .automations .automation .box .box-right  { margin-left: 0px;  }

        .automations .automation .stats { flex-direction: column; }
        .trigger-icon { display: none; }
    }
}
</style>
